// Visitor platform detection — the single answer to "what device is looking at
// this page", used to adapt every desktop-download CTA (see DownloadCta.tsx).
//
// WHY THIS EXISTS: 71% of "Download for macOS" clicks come from iOS/Android
// (Meta ad traffic) and another 5% from Windows — devices that cannot run the
// dmg at all. The funnel audit (Aug 2026) showed those visitors rage-tapping
// the button, misusing the Windows waitlist as "notify me", or leaving. The
// CTA now routes each platform to something that can actually succeed.
//
// Detection is heuristic by nature. Two traps handled explicitly:
//   • iPadOS lies: Safari on iPad reports "MacIntel" with a desktop UA. The
//     tell is maxTouchPoints > 1 on a "Mac" — real Macs report 0.
//   • Chromium's userAgentData.platform is the modern, unfrozen signal — prefer
//     it when present, fall back to the UA string elsewhere (Safari/Firefox).
//
// SSR/prerender-safe: with no window it answers 'other'; components default to
// the mac variant and re-detect in an effect after mount, so prerendered HTML
// never bakes in a phone-specific CTA.

export type VisitorPlatform = 'mac' | 'windows' | 'linux' | 'ios' | 'android' | 'other';

export const detectPlatform = (): VisitorPlatform => {
  try {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return 'other';

    const nav = navigator as Navigator & {
      userAgentData?: { platform?: string; mobile?: boolean };
    };
    const ua = navigator.userAgent || '';
    const uadPlatform = (nav.userAgentData?.platform || '').toLowerCase();

    // Android first — some Android browsers also mention "Linux" in the UA.
    if (uadPlatform === 'android' || /android/i.test(ua)) return 'android';

    // iPhone/iPod/iPad by UA, plus the iPadOS-desktop-mode case: claims Mac,
    // but Macs have no touch screen.
    if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
    const claimsMac = uadPlatform === 'macos' || /mac/i.test(ua);
    if (claimsMac && (navigator.maxTouchPoints ?? 0) > 1) return 'ios';

    if (claimsMac) return 'mac';
    if (uadPlatform === 'windows' || /windows/i.test(ua)) return 'windows';
    if (uadPlatform === 'chrome os' || /cros/i.test(ua)) return 'other';
    if (uadPlatform === 'linux' || /linux|x11/i.test(ua)) return 'linux';
    return 'other';
  } catch {
    return 'other'; // detection must never break a CTA
  }
};

/** True for platforms that can install the desktop app today. */
export const canInstallDesktop = (p: VisitorPlatform): boolean => p === 'mac';
